import styled, { createGlobalStyle, type DefaultTheme } from "styled-components";
import { BREAKPOINT_MAX } from "../../styles/responsiveBreakpoints";
import {
  FLIGHT_CASES_ANALYST_FILTER_LISTBOX_ID,
  FLIGHT_CASES_FILTER_BORDER_FOCUS,
  FLIGHT_CASES_FILTER_BORDER_HOVER,
  FLIGHT_CASES_FILTER_LIST_OPTION_HOVER_BG,
  FLIGHT_CASES_STATUS_FILTER_LISTBOX_ID,
} from "./flightChangesCasesFilterControlSx";

/** Listas en portal: fuerza hover #5c5c5c @ 8% en opciones no seleccionadas (Combobox e Hangar Select). */
export const FilterDropdownOptionHoverStyles = createGlobalStyle`
  #${FLIGHT_CASES_STATUS_FILTER_LISTBOX_ID} [role="option"]:hover:not([aria-disabled="true"]):not([aria-selected="true"]),
  #${FLIGHT_CASES_ANALYST_FILTER_LISTBOX_ID} [role="option"]:hover:not([aria-disabled="true"]):not([aria-selected="true"]) {
    background-color: ${FLIGHT_CASES_FILTER_LIST_OPTION_HOVER_BG} !important;
  }
`;

const filterBorderSmRem = (theme: DefaultTheme) => {
  const px = theme.tokens?.border?.width?.sm ?? 1;
  return `${Math.round((px / 16) * 1000) / 1000}rem`;
};

const filterBorderMdRem = (theme: DefaultTheme) => {
  const px = theme.tokens?.border?.width?.md ?? 2;
  return `${Math.round((px / 16) * 1000) / 1000}rem`;
};

export const Container = styled.div`
  padding: 0;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

export const Card = styled.div`
  background: ${({ theme }) => theme.tokens.color.background.primary};
  border-radius: 0.25rem;
  padding: 2rem;
  margin-top: 2.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid ${({ theme }) => theme.tokens.color.border.primary};
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: ${BREAKPOINT_MAX.xl}px) {
    padding: 1.5rem;
    margin-top: 1.5rem;
  }

  @media (max-width: ${BREAKPOINT_MAX.md}px) {
    padding: 1rem;
    margin-top: 1.25rem;
  }
`;

export const Header = styled.div`
  margin-bottom: 2rem;

  @media (max-width: ${BREAKPOINT_MAX.md}px) {
    margin-bottom: 1.25rem;
  }
`;

export const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: ${BREAKPOINT_MAX.lg}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

export const FilterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
`;

export const FilterFieldColumn = styled(FilterColumn)`
  width: 230px;
  min-width: 230px;
  flex-shrink: 0;
  box-sizing: border-box;

  @media (max-width: ${BREAKPOINT_MAX.lg}px) {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    flex: 1 1 auto;
  }
`;

/** Estados: misma anchura fija que AWB y Analista (230px). */
export const FilterStatusesFieldColumn = styled(FilterFieldColumn)``;

/**
 * Estados (Combobox) y Analista (Select): un solo borde #0A0E80 en focus (shorthand + sin outline
 * en el input interno; evita mezclar con tokens tipo #0d12ab del DS).
 */
export const FilterInputsShell = styled.div`
  display: contents;

  & [role="combobox"]:where(:not([aria-disabled="true"])) {
    border: ${({ theme }) => `${filterBorderSmRem(theme)} solid ${theme.tokens.color.border.primary}`} !important;
    outline: none !important;
    box-shadow: none !important;
  }

  & [role="combobox"]:where(:not([aria-disabled="true"])):hover:not(:focus-within):not(
      [aria-expanded="true"]
    ) {
    border: ${({ theme }) => `${filterBorderSmRem(theme)} solid ${FLIGHT_CASES_FILTER_BORDER_HOVER}`} !important;
    outline: none !important;
    box-shadow: none !important;
  }

  & [role="combobox"]:where(:not([aria-disabled="true"])):focus-within,
  & [role="combobox"]:where(:not([aria-disabled="true"]))[aria-expanded="true"] {
    border: ${({ theme }) =>
      `${filterBorderMdRem(theme)} solid ${FLIGHT_CASES_FILTER_BORDER_FOCUS}`} !important;
    outline: none !important;
    box-shadow: none !important;
  }

  & [role="combobox"] input:focus,
  & [role="combobox"] input:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }
`;

export const FilterLabel = styled.span`
  display: block;
  margin: 0;
  padding: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.tokens.color.text.secondary};
  line-height: 1.2;
  min-height: 17px;
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

export const FilterActionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  justify-content: flex-start;
  flex-shrink: 0;

  @media (max-width: ${BREAKPOINT_MAX.lg}px) {
    width: 100%;
    align-self: stretch;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
`;

export const FilterActionsLabelSpacer = styled.span`
  display: block;
  min-height: 17px;
  line-height: 1.2;

  @media (max-width: ${BREAKPOINT_MAX.lg}px) {
    display: none;
  }
`;

export const Title = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  color: ${({ theme }) => theme.tokens.color.text.primary};
  margin: 0 0 3rem 0;
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

export const TableContainer = styled.div`
  border-radius: 0.25rem;
  overflow: hidden;
  background-color: ${({ theme }) => theme.tokens.color.background.primary};
  max-height: 600px;
  overflow-y: auto;
  height: calc(100vh - 350px);
  display: flex;
`;

export const DateCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const RegistrationTimeText = styled.span`
  font-size: 0.875rem;
`;

export const SubtitleTitle = styled.span`
  font-size: 1.5rem;
`;

export const RegistrationTimezoneText = styled.span`
  font-size: 1rem;
  margin: 0.25rem 0 0 0;
  display: block;
`;

export const NotificationLegendText = styled.span`
  font-size: 0.75rem;

  & span,
  & > * {
    font-size: 0.75rem !important;
  }
`;

export const AlertWrapper = styled.div`
  width: 100%;
  margin: 1rem 0;
  display: block;

  & > * {
    width: 100% !important;
    min-width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  min-height: 400px;
  gap: 1rem;
`;

export const SkeletonFiltersRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: ${BREAKPOINT_MAX.lg}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

export const SkeletonFilterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex-shrink: 0;
  min-width: 0;
`;

export const SkeletonFilterFieldColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  width: 230px;
  min-width: 230px;
  flex-shrink: 0;
  box-sizing: border-box;

  @media (max-width: ${BREAKPOINT_MAX.lg}px) {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    flex: 1 1 auto;
  }
`;

export const SkeletonRowCell = styled.div`
  padding: 0.75rem 1rem;
  & > * {
    width: 100%;
    border-radius: 0.25rem;
  }
`;

export const LoadingText = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.tokens.color.text.secondary};
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
  border-radius: 0;
  margin: 1rem 0;
  border: 2px solid ${({ theme }) => theme.tokens.color.border.primary || '#E5E7EB'};
  border-radius: 0.5rem;
`;

export const UnavailableContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1.5rem;
  gap: 1rem;
  text-align: center;
  margin: 1rem 0;
  background: ${({ theme }) => theme.tokens.color.background.primary};
`;

export const UnavailableIllustration = styled.img`
  width: 136px;
  height: 136px;
  display: block;
  object-fit: contain;
`;

export const ErrorText = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.tokens.color.text.error};
  margin: 0;
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

export const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
  background: ${({ theme }) => theme.tokens.color.background.primary};
  border: 1px solid ${({ theme }) => theme.tokens.color.border.primary};
  border-radius: 1rem;
  margin: 1rem 0;
  min-height: 300px;
`;

export const EmptyText = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.tokens.color.text.secondary};
  margin: 0;
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

export const NotificationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.2rem;
`;

export const RiskDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.tokens.color.icon.error};
  display: inline-block;
  margin-bottom: 0.8rem;
`;

export const IconCircle = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.tokens.color.surface.warningSoft};
  display: flex;
  align-items: center;
  justify-content: center;

  svg, .MuiSvgIcon-root {
    color: ${({ theme }) => theme.tokens.color.text.primary};
  }
`;

export const EmptyIconCircle = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.tokens.color.surface.primary};
  display: flex;
  align-items: center;
  justify-content: center;

  svg, .MuiSvgIcon-root {
    color: ${({ theme }) => theme.tokens.color.text.primary};
  }
`;

export const ArrowIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.tokens.color.text.primary};
  
  svg {
    font-size: 1rem;
    width: 2rem;
    height: 2rem;
    font-weight: bold;
  }
`;

export const ReloadButton = styled.div`
  margin-top: 1rem;
`;

export const EmptyStateIllustration = styled.img`
  width: 136px;
  height: 136px;
  display: block;
  object-fit: contain;
`;

export const EmptyStateInTable = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1.5rem;
  gap: 1rem;
  text-align: center;
  min-height: 280px;
  background: ${({ theme }) => theme.tokens.color.background.primary};
`;

export const AssignLink = styled.button<{ disabled?: boolean }>`
  background: none;
  border: none;
  color: ${({ theme, disabled }) =>
    disabled ? theme.tokens.color.text.disabled : theme.tokens.color.interaction.softDefault};
  font-size: 0.875rem;
  font-weight: 700;
  cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
  padding: 0;
  text-decoration: underline;
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;

  &:hover {
    color: ${({ theme, disabled }) =>
      disabled ? theme.tokens.color.text.disabled : theme.tokens.color.interaction.softHover};
    text-decoration: underline;
  }
`;

/** Fixed host for Hangar Alert (error toasts, US-004). */
export const SnackbarFixedAnchor = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1001;
  max-width: min(550px, calc(100vw - 48px));
  box-sizing: border-box;
`;

/** Semantic table shell (Hangar DS — tokens + typography; no MUI Table). */
export const DataTableContainer = styled.div`
  overflow-x: auto;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  border: none;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
`;

export const DataTable = styled.table`
  width: 100%;
  min-width: 880px;
  border-collapse: separate;
  border-spacing: 0;
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

export const DataTableHead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 1;
`;

export const DataTableBody = styled.tbody``;

export const DataTableRow = styled.tr<{ $clickable?: boolean }>`
  &:hover {
    background-color: ${({ theme }) => theme.tokens?.color?.surface?.secondary ?? 'rgba(0, 0, 0, 0.04)'};
  }
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
`;

export const DataTableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  vertical-align: middle;
  border-bottom: 1px solid ${({ theme }) => theme.tokens?.color?.border?.primary ?? '#e0e0e0'};
  background-color: ${({ theme }) => theme.tokens?.color?.background?.primary ?? '#ffffff'};
  font-weight: inherit;

  @media (max-width: ${BREAKPOINT_MAX.md}px) {
    padding: 12px 10px;
  }
`;

export const DataTableCell = styled.td<{ $variant?: 'default' | 'checkbox' }>`
  padding: ${(p) => (p.$variant === 'checkbox' ? '4px 16px' : '16px')};
  text-align: left;
  vertical-align: middle;
  border-bottom: 1px solid ${({ theme }) => theme.tokens?.color?.border?.primary ?? '#e0e0e0'};

  @media (max-width: ${BREAKPOINT_MAX.md}px) {
    padding: ${(p) => (p.$variant === 'checkbox' ? '4px 10px' : '12px 10px')};
  }
`;

export const SortHeaderButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font: inherit;
  color: inherit;
  text-align: left;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.tokens?.color?.border?.focusStandard ?? '#0a0e80'};
    outline-offset: 2px;
  }
`;

export const SortIconInactive = styled.span`
  display: inline-flex;
  opacity: 0.3;
  svg {
    display: block;
  }
`;

export const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 1rem;
  row-gap: 0.5rem;

  @media (max-width: ${BREAKPOINT_MAX.md}px) {
    justify-content: center;
    width: 100%;
  }
`;

export const PaginationRowsGroup = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

export const PaginationNativeSelect = styled.select`
  font-size: 0.875rem;
  font-family: inherit;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.tokens?.color?.border?.primary ?? '#e0e0e0'};
  background: ${({ theme }) => theme.tokens?.color?.background?.primary ?? '#fff'};
  color: ${({ theme }) => theme.tokens?.color?.text?.primary ?? '#1a1a1a'};
`;

export const PaginationNav = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`;

export const PaginationToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
  width: 100%;
  min-height: 52px;
  padding: 0 14px;
  border-top: 1px solid ${({ theme }) => theme.tokens?.color?.border?.primary ?? '#e0e0e0'};
  background-color: ${({ theme }) => theme.tokens?.color?.background?.primary};
  box-sizing: border-box;
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;

  @media (max-width: ${BREAKPOINT_MAX.lg}px) {
    justify-content: space-between;
  }

  @media (max-width: ${BREAKPOINT_MAX.md}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    padding-top: 10px;
    padding-bottom: 10px;
    min-height: 0;
  }
`;

export const NotificationDeadlineLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'LATAM Sans Regular', 'LATAM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  color: ${({ theme }) => theme.tokens?.color?.text?.secondary ?? '#6a6a6a'};
  flex-shrink: 0;

  @media (max-width: ${BREAKPOINT_MAX.md}px) {
    width: 100%;
  }
`;

export const NotificationDeadlineDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.tokens.color.icon.error};
  flex-shrink: 0;
`;

export const Footer = styled.footer`
  width: 100%;
  height: 120px;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem 0;
`;